import { doc, getDoc } from 'firebase/firestore';

import { db } from './firebase';
import { EVENT_DEFINITIONS_COLLECTION } from './firestoreCollections';
import { formatBreweryConsumptionLabel } from './brewery/formatBreweryConsumptionLabel';
import {
  CHECKIN_TYPE_BEER,
  CHECKIN_TYPE_BREWERY,
  CHECKIN_TYPE_EVENT,
  mapCheckinDoc,
} from './mapCheckinDoc';

const UNKNOWN_BEER = 'Unknown Beer';
const UNKNOWN_BREWERY = 'Unknown Brewery';
const UNKNOWN_EVENT = 'Unknown event';
const UNKNOWN_USER = 'Unknown User';

/**
 * @param {Map<string, Promise<object | null>>} cache
 * @param {string} collectionName
 * @param {string} docId
 * @returns {Promise<object | null>}
 */
function getCachedDocData(cache, collectionName, docId) {
  if (typeof docId !== 'string' || docId.length === 0) {
    return null;
  }
  if (cache.has(docId)) {
    return cache.get(docId);
  }
  const request = getDoc(doc(db, collectionName, docId))
    .then((snapshot) => (snapshot.exists() ? snapshot.data() : null))
    .catch((fetchError) => {
      console.error(`Error fetching ${collectionName}/${docId}:`, fetchError);
      return null;
    });
  cache.set(docId, request);
  return request;
}

/**
 * @param {unknown} value
 * @returns {string | undefined}
 */
function trimId(value) {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * @param {object | null | undefined} breweryData
 * @returns {string}
 */
function resolveBreweryName(breweryData) {
  if (!breweryData || typeof breweryData !== 'object') {
    return UNKNOWN_BREWERY;
  }
  const name = typeof breweryData.name === 'string' ? breweryData.name.trim() : '';
  return name.length > 0 ? name : UNKNOWN_BREWERY;
}

/**
 * @param {object | null | undefined} breweryData
 * @returns {{ breweryName: string, breweryLocationLabel: string }}
 */
function buildBreweryFields(breweryData) {
  const breweryName = resolveBreweryName(breweryData);
  const breweryLocationLabel = breweryData
    ? formatBreweryConsumptionLabel(breweryData)
    : breweryName;
  return { breweryName, breweryLocationLabel };
}

/**
 * @param {string | undefined} docId
 * @param {string} collectionName
 * @param {object | null} caches
 * @param {string} cacheKey
 * @returns {Promise<object | null>}
 */
async function fetchDocData(docId, collectionName, caches, cacheKey) {
  if (!docId) {
    return null;
  }
  if (caches?.[cacheKey]) {
    return getCachedDocData(caches[cacheKey], collectionName, docId);
  }
  try {
    const snapshot = await getDoc(doc(db, collectionName, docId));
    return snapshot.exists() ? snapshot.data() : null;
  } catch (fetchError) {
    console.error(`Error fetching ${collectionName}/${docId}:`, fetchError);
    return null;
  }
}

/**
 * @param {string | undefined} userId
 * @param {object | null} caches
 * @returns {Promise<string>}
 */
async function resolveUserName(userId, caches) {
  if (!userId) {
    return UNKNOWN_USER;
  }
  const userData = await fetchDocData(userId, 'users', caches, 'userCache');
  if (!userData) {
    return UNKNOWN_USER;
  }
  return userData.userName || userData.displayName || UNKNOWN_USER;
}

/**
 * @param {string | undefined} eventId
 * @param {object | null} caches
 * @returns {Promise<string>}
 */
async function resolveEventName(eventId, caches) {
  if (!eventId) {
    return UNKNOWN_EVENT;
  }
  const eventData = await fetchDocData(
    eventId,
    EVENT_DEFINITIONS_COLLECTION,
    caches,
    'eventCache',
  );
  const resolvedEventName = eventData && typeof eventData.name === 'string'
    ? eventData.name.trim()
    : '';
  return resolvedEventName.length > 0 ? resolvedEventName : UNKNOWN_EVENT;
}

/**
 * @param {string | undefined} breweryId
 * @param {object | null} caches
 * @returns {Promise<{ breweryName: string, breweryLocationLabel: string }>}
 */
async function resolveBreweryFields(breweryId, caches) {
  if (!breweryId) {
    return {
      breweryName: UNKNOWN_BREWERY,
      breweryLocationLabel: UNKNOWN_BREWERY,
    };
  }
  const breweryData = await fetchDocData(breweryId, 'breweries', caches, 'breweryCache');
  return buildBreweryFields(breweryData);
}

/**
 * @param {string | undefined} beerId
 * @param {string | undefined} breweryId
 * @param {object | null} caches
 * @returns {Promise<{
 *   beerName: string,
 *   breweryId: string | undefined,
 *   breweryName: string,
 *   breweryLocationLabel: string,
 * }>}
 */
async function resolveBeerCheckinFields(beerId, breweryId, caches) {
  let beerName = UNKNOWN_BEER;
  let resolvedBreweryId = breweryId;

  if (beerId) {
    const beerData = await fetchDocData(beerId, 'beer', caches, 'beerCache');
    const resolvedBeerName = beerData && typeof beerData.name === 'string'
      ? beerData.name.trim()
      : '';
    beerName = resolvedBeerName.length > 0 ? resolvedBeerName : UNKNOWN_BEER;
    if (!resolvedBreweryId) {
      resolvedBreweryId = trimId(beerData?.breweryId);
    }
  }

  const breweryFields = await resolveBreweryFields(resolvedBreweryId, caches);
  return {
    beerName,
    breweryId: resolvedBreweryId,
    breweryName: breweryFields.breweryName,
    breweryLocationLabel: breweryFields.breweryLocationLabel,
  };
}

/**
 * @param {import('firebase/firestore').QueryDocumentSnapshot} checkinDoc
 * @param {object} [caches]
 * @param {Map<string, Promise<object | null>>} [caches.breweryCache]
 * @param {Map<string, Promise<object | null>>} [caches.userCache]
 * @param {Map<string, Promise<object | null>>} [caches.beerCache]
 * @param {Map<string, Promise<object | null>>} [caches.eventCache]
 * @returns {Promise<object>}
 */
export async function enrichCheckinDoc(checkinDoc, caches = null) {
  const checkinData = checkinDoc.data();
  const checkinType = typeof checkinData.type === 'string'
    ? checkinData.type.trim().toLowerCase()
    : CHECKIN_TYPE_BREWERY;

  const userName = await resolveUserName(trimId(checkinData.uid), caches);

  let beerName = UNKNOWN_BEER;
  let breweryId = trimId(checkinData.breweryId);
  let breweryName = UNKNOWN_BREWERY;
  let breweryLocationLabel = UNKNOWN_BREWERY;
  let eventName = UNKNOWN_EVENT;

  if (checkinType === CHECKIN_TYPE_EVENT) {
    eventName = await resolveEventName(trimId(checkinData.eventId), caches);
  } else if (checkinType === CHECKIN_TYPE_BEER) {
    const beerFields = await resolveBeerCheckinFields(
      trimId(checkinData.beerId),
      breweryId,
      caches,
    );
    beerName = beerFields.beerName;
    breweryId = beerFields.breweryId;
    breweryName = beerFields.breweryName;
    breweryLocationLabel = beerFields.breweryLocationLabel;
  } else {
    const breweryFields = await resolveBreweryFields(breweryId, caches);
    breweryName = breweryFields.breweryName;
    breweryLocationLabel = breweryFields.breweryLocationLabel;
  }

  return mapCheckinDoc({
    checkinId: checkinDoc.id,
    checkinData: breweryId ? { ...checkinData, breweryId } : checkinData,
    beerName,
    eventName,
    breweryName,
    breweryLocationLabel,
    userName,
  });
}

/**
 * @param {import('firebase/firestore').QueryDocumentSnapshot[]} checkinDocs
 * @returns {Promise<object[]>}
 */
export function enrichCheckinDocs(checkinDocs) {
  const caches = {
    breweryCache: new Map(),
    userCache: new Map(),
    beerCache: new Map(),
    eventCache: new Map(),
  };
  return Promise.all(
    checkinDocs.map((checkinDoc) => enrichCheckinDoc(checkinDoc, caches)),
  );
}
